import React, { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import type CrmFundraisingDonationNature from '@/types/serializers/CrmFundraisingDonationNature'

interface EditDonationNatureSheetProps {
  nature: CrmFundraisingDonationNature | null
  onOpenChange: (open: boolean) => void
}

export function EditDonationNatureSheet({
  nature,
  onOpenChange,
}: EditDonationNatureSheetProps) {
  const form = useForm({ donation_nature: { name: '' } })
  const { setData, patch, processing, errors, reset } = form

  // Dep on id only: avoids re-seeding (and clobbering edits) when the parent
  // re-renders with the same record but a new object reference.
  useEffect(() => {
    if (nature) {
      setData('donation_nature', { name: nature.name })
    }
  }, [nature?.id])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!nature) return
    patch(`/fundraising/donation_natures/${nature.id}`, {
      onSuccess: () => {
        reset()
        onOpenChange(false)
      },
    })
  }

  return (
    <Sheet open={!!nature} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] sm:max-w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Renommer la nature de don</SheetTitle>
          <SheetDescription>
            Seul le nom affiche change. Le code reste inchange.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="edit_dn_name">Nom *</Label>
            <Input
              id="edit_dn_name"
              value={form.data.donation_nature.name}
              onChange={(e) =>
                setData('donation_nature', { name: e.target.value })
              }
              required
              autoFocus
            />
            {/* The update interaction merges model errors as flat attribute
                keys (`errors.add(e.attribute, ...)`), so read `name` directly. */}
            {errors['name'] && (
              <p className="text-sm text-destructive">
                {errors['name']}
              </p>
            )}
          </div>

          {nature && (
            <div className="space-y-2">
              <Label>Code</Label>
              <Input value={nature.code ?? '-'} disabled className="font-mono text-xs" />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={processing} className="flex-1">
              {processing ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
